import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { MultilangText, ServiceField } from "../../models/Service";
import { compilePattern, testsAny } from "../../utils/safePattern";
import { cleanPhoneInput, displayPhone } from "../../utils/displayMask";
import useMaskedInput from "../../hooks/useMaskedInput";
import { fieldSx } from "../inputs/TextInput";
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  phoneCountry as findCountry,
  phonePatternCandidates,
} from "../../constants/phoneCountries";

// What this renderer can actually draw. CORE declares a fourth value, QRCODE,
// which needs a scanner this portal does not have -- so it is deliberately
// absent here and takes the unsupported path with anything CORE adds later.
const RENDERABLE_INPUTS = ["NUMERICAL", "ALPHA_CS", "ALPHA_CI"] as const;

type ConfiguredFieldsProps = {
  fields: ServiceField[];
  values?: Record<string, string>;
  onChange?: (fieldId: string, value: string) => void;
  // onSupported fields cannot be drawn, so the parent can refuse to submit since there no
  // fields to submit this in. 
  onUnsupported?: (blockingFieldIds: string[]) => void;
  // Incomplete fields are mandatory fields that are blank or have a failing regex
  onIncomplete?: (incompleteFieldIds: string[]) => void;
  // Text the portal adds under a field, by code, beside the service's own
  // hint -- never in place of it.
  notes?: Record<string, string>;
  // The country a CUSPHONE answer is grouped and cleaned for.
  phoneCountry?: string;
};

type AnswerProps = Omit<TextFieldProps, "onChange" | "value"> & {
  value: string;
  onValueChange: (value: string) => void;
};

// A phone answer uses the payer's control: a country selector (CM by
// default), its dial code beside the box, grouped display, digits in state.
const PhoneAnswer = ({
  value,
  onValueChange,
  country: initialCountry,
  ...props
}: AnswerProps & { country: string }) => {
  const { t } = useTranslation();
  const [country, setCountry] = useState(initialCountry);
  const entry = findCountry(country);
  const masked = useMaskedInput({
    value,
    clean: raw => cleanPhoneInput(raw, country),
    display: digits => displayPhone(digits, country),
    onValueChange,
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <TextField
          select
          fullWidth
          sx={fieldSx}
          id={`${props.id}_country`}
          label={t("schoolfee.contact.country_label")}
          value={country}
          onChange={event => setCountry(event.target.value)}
        >
          {PHONE_COUNTRIES.map(option => (
            <MenuItem key={option.code} value={option.code}>
              {`${option.code} ${option.dialCode}`}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={8}>
        <TextField
          {...props}
          sx={fieldSx}
          type="tel"
          inputMode="numeric"
          placeholder={entry ? displayPhone(entry.example, country) : undefined}
          slotProps={{
            input: {
              startAdornment: entry && (
                <InputAdornment position="start">{entry.dialCode}</InputAdornment>
              ),
            },
          }}
          inputRef={masked.inputRef}
          value={masked.value}
          onChange={masked.onChange}
        />
      </Grid>
    </Grid>
  );
};

const helperWithNote = (primary: string, note?: string): ReactNode =>
  note ? (
    <>
      {primary && (
        <Box component="span" sx={{ display: "block" }}>
          {primary}
        </Box>
      )}
      <Box component="span" sx={{ display: "block" }}>
        {note}
      </Box>
    </>
  ) : (
    primary
  );

// An empty label on a payment form is worse than a technical identifier, so
// this never returns "": exact locale, then any locale CORE sent, then the id.
export const resolveText = (
  entries: MultilangText[] | undefined,
  language: string,
  fallback = ""
): string => {
  const exact = entries?.find(e => e.lang === language)?.value;
  if (exact) return exact;

  const any = entries?.find(e => !!e.value)?.value;
  return any || fallback;
};

const isRenderable = (input: string): boolean =>
  (RENDERABLE_INPUTS as readonly string[]).includes(input);

// A phone answer is held as national digits, but CORE writes its pattern
// against the number as the service sees it. Every other field's pattern is
// tested against exactly what the payer has entered.
const patternCandidates = (
  field: ServiceField,
  value: string,
  country: string
): string[] =>
  field.fieldId === "CUSPHONE"
    ? phonePatternCandidates(value, country)
    : [value];


// The value a field currently holds, the same precedence the render loop
// uses: the caller's own state wins when it has an entry, so a controlled
// `values` prop is authoritative over what has merely been typed locally.
const fieldValue = (
  values: Record<string, string> | undefined,
  edited: Record<string, string>,
  fieldId: string
): string => values?.[fieldId] ?? edited[fieldId] ?? "";

const ConfiguredFields = ({
  fields,
  values,
  onChange,
  onUnsupported,
  onIncomplete,
  notes,
  phoneCountry = DEFAULT_PHONE_COUNTRY,
}: ConfiguredFieldsProps) => {
  const { t, i18n } = useTranslation();
  const [edited, setEdited] = useState<Record<string, string>>({});

  const unsupported = useMemo(
    () =>
      fields.filter(f => f.mandatory && !isRenderable(f.input)).map(f => f.fieldId),
    [fields]
  );

  const incomplete = useMemo(
    () =>
      fields
        .filter(f => {
          if (!f.mandatory || !isRenderable(f.input)) return false;

          const value = fieldValue(values, edited, f.fieldId);
          if (value.trim() === "") return true;

          const pattern = compilePattern(f.regex);
          return (
            !!pattern &&
            !testsAny(pattern, patternCandidates(f, value, phoneCountry))
          );
        })
        .map(f => f.fieldId),
    [fields, values, edited, phoneCountry]
  );

  // Both callbacks are held in refs rather than left out of the dependency
  // arrays. Omitting them stops the loop an inline arrow would cause, but it
  // also pins the FIRST callback forever: a parent whose handler closes over
  // changing state would keep being called with a stale one.
  const onUnsupportedRef = useRef(onUnsupported);
  useEffect(() => {
    onUnsupportedRef.current = onUnsupported;
  });
  const onIncompleteRef = useRef(onIncomplete);
  useEffect(() => {
    onIncompleteRef.current = onIncomplete;
  });

  const unsupportedKey = unsupported.join(",");
  useEffect(() => {
    onUnsupportedRef.current?.(unsupportedKey ? unsupportedKey.split(",") : []);
  }, [unsupportedKey]);

  const incompleteKey = incomplete.join(",");
  useEffect(() => {
    onIncompleteRef.current?.(incompleteKey ? incompleteKey.split(",") : []);
  }, [incompleteKey]);

  if (fields.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {fields.map((field, index) => {
        // A code may be declared twice; each occurrence needs its own id so
        // its label still points at its own input.
        const occurrence = fields
          .slice(0, index)
          .filter(f => f.fieldId === field.fieldId).length;
        const suffix = occurrence > 0 ? `_${occurrence + 1}` : "";
        const label = resolveText(field.label, i18n.language, field.fieldId);
        const hint = resolveText(field.hint, i18n.language);

        if (!isRenderable(field.input)) {
          // Every unsupported field is visible as an alert; a MANDATORY one
          // also says the service cannot be paid without it, so "cannot be
          // shown" and "cannot be paid" stay distinguishable to the payer.
          const notice = `${label} — ${t(
            "configuredFields.unsupported.notice"
          )}`;

          return (
            <Alert
              key={`${field.fieldId}${suffix}`}
              severity={field.mandatory ? "warning" : "info"}
              data-testid={`configured_field_unsupported_${field.fieldId}${suffix}`}
            >
              {field.mandatory
                ? `${notice} ${t("configuredFields.unsupported.blocking")}`
                : notice}
            </Alert>
          );
        }

        const value = fieldValue(values, edited, field.fieldId);
        const pattern = compilePattern(field.regex);
        const invalid =
          !!pattern &&
          value.length > 0 &&
          !testsAny(pattern, patternCandidates(field, value, phoneCountry));

        const setValue = (next: string) => {
          setEdited(prev => ({ ...prev, [field.fieldId]: next }));
          onChange?.(field.fieldId, next);
        };
        const shared = {
          id: `configured_field_${field.fieldId}${suffix}`,
          "data-testid": `configured_field_${field.fieldId}${suffix}`,
          label,
          required: field.mandatory,
          error: invalid,
          helperText: helperWithNote(
            invalid ? t("configuredFields.invalid") : hint,
            notes?.[field.fieldId]
          ),
          fullWidth: true,
          size: "small" as const,
        };

        if (field.fieldId === "CUSPHONE") {
          return (
            <PhoneAnswer
              key={`${field.fieldId}${suffix}`}
              {...shared}
              country={phoneCountry}
              value={value}
              onValueChange={setValue}
            />
          );
        }

        return (
          <TextField
            key={`${field.fieldId}${suffix}`}
            {...shared}
            sx={fieldSx}
            value={value}
            // NUMERICAL is a keypad hint, not a filter: the regex CORE sent is
            // what decides validity, and a type=number field would swallow it.
            inputMode={field.input === "NUMERICAL" ? "numeric" : "text"}
            onChange={event => setValue(event.target.value)}
          />
        );
      })}
    </Box>
  );
};

export default ConfiguredFields;
